import { RiShieldLine, RiUser3Line } from '@remixicon/react';
import type { ComponentProps, ReactNode } from 'react';
import { useState } from 'react';

import { cn } from '@playbook/utils';

import css from './styles.module.css';

export type AvatarVariant = 'picture' | 'club' | 'initials' | 'user' | 'shield';

const DefaultAvatarVariant: AvatarVariant = 'picture' as const;

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const DefaultAvatarSize: AvatarSize = 'sm' as const;

type AvatarEventHandlers = {
	[K in keyof ComponentProps<'div'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'div'>[K];
};

interface AvatarDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface AvatarProperties extends AvatarEventHandlers, AvatarDataAttributes {
	variant?: AvatarVariant;
	size?: AvatarSize;

	imageUrl?: string;
	alt?: string;
	initials?: string;
}

/**
 *	`user` and `shield` are the two variants whose content is the variant: the
 *	glyph is not a caller's choice, so it is named here rather than taken as a
 *	slot. Figma draws `user-3-line` and `shield-line`, which are these two.
 */
function avatarContent(
	variant: AvatarVariant,
	imageUrl: string | undefined,
	alt: string,
	initials: string | undefined,
	onImageError: () => void
): ReactNode {
	switch (variant) {
		case 'picture':
		case 'club':
			return imageUrl ? (
				<img
					className={css.image}
					src={imageUrl}
					alt={alt}
					loading="lazy"
					onError={onImageError}
				/>
			) : null;
		case 'initials':
			return <span className={css.lettering}>{initials}</span>;
		case 'user':
			return <RiUser3Line />;
		case 'shield':
			return <RiShieldLine />;
	}
}

export function Avatar({
	variant = DefaultAvatarVariant,
	size = DefaultAvatarSize,

	imageUrl,
	alt = '',
	initials,

	...props
}: AvatarProperties): ReactNode {
	const [failedImageUrl, setFailedImageUrl] = useState<string | undefined>();

	let resolvedVariant: AvatarVariant = variant;

	const imageFailed = !!imageUrl && failedImageUrl === imageUrl;

	if (variant === 'picture' && (!imageUrl || imageFailed)) {
		resolvedVariant = initials ? 'initials' : 'user';
	} else if (variant === 'club' && imageFailed) {
		resolvedVariant = 'shield';
	}

	const className: string = cn(css.avatar, css[resolvedVariant], css[size]);

	const handleImageError = () => {
		setFailedImageUrl(imageUrl);
	};

	return (
		<div
			{...props}
			data-variant={resolvedVariant}
			data-size={size}
			className={className}>
			{avatarContent(
				resolvedVariant,
				imageUrl,
				alt,
				initials,
				handleImageError
			)}
		</div>
	);
}
